import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Read the csrf_token cookie (it's not httponly so JS can access it)
function getCsrfToken() {
  const match = document.cookie.match(/(^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[2]) : '';
}

// Attach x-csrf-token header to every mutating request automatically
axios.interceptors.request.use(config => {
  const method = (config.method || '').toUpperCase();
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    const token = getCsrfToken();
    if (token) {
      config.headers['x-csrf-token'] = token;
    }
  }
  return config;
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(null);
  };

  const hasRole = (minRole) => {
    const hierarchy = { super_admin: 3, admin: 2, agent: 1 };
    const userLevel = hierarchy[user?.role] || 0;
    const requiredLevel = hierarchy[minRole] || 0;
    return userLevel >= requiredLevel;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};
